import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Trash2,
  Upload,
  QrCode,
  FileText,
  Users,
  Menu,
  X,
  CheckCircle,
  AlertCircle,
  LogOut,
  ScanLine,
  FileCheck,
  TrendingUp,
  Globe,
  Building2,
  Stethoscope,
  Factory,
  ArrowRight,
  Plus,
  Sparkles,
  Search,
  Lightbulb,
  Leaf,
  Calculator,
  Coins,
  TrendingDown,
  Info,
  Award,
  ShieldCheck,
  Hash,
  Share2,
  Download,
  Camera,
  FileSearch,
  CheckSquare,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

// --- GEMINI API CONFIGURATION ---
const apiKey = ""; // API Key injected by environment

const callGemini = async (prompt) => {
  try {
    if (!apiKey) {
        // Fallback for demo if no key is present
        await new Promise(resolve => setTimeout(resolve, 1500));
        return "Simulation: The AI would provide specific insights here based on your waste data. Ensure an API key is configured for live responses.";
    }

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }],
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`API call failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data.candidates?.[0]?.content?.parts?.[0]?.text || "No response generated.";
  } catch (error) {
    console.error("Gemini API Error:", error);
    return "Error connecting to AI service. Please try again.";
  }
};

// --- SEED DATA (In-memory DB) ---
const SEED_DATA = {
  institutions: [
    { id: "inst-001", name: "St. Xavier College, Kochi", city: "Kochi", students: 1800, staff: 150 },
    { id: "inst-002", name: "Marina Arts & Science College", city: "Kochi", students: 950, staff: 60 }
  ],
  recyclers: [
    { id: "rec-001", name: "Urban E-Cycle Hub", categories: ["E-Waste", "Metals"], contact: { phone: "+91-8123456789", email: "ops@uecycle.example" } },
    { id: "rec-002", name: "Polymer Pro Ltd", categories: ["Plastic"], contact: { phone: "+91-8123456790", email: "info@polypro.example" } }
  ],
  waste_logs: [
    { id: "log-1001", institution_id: "inst-001", category: "Food Waste", weight_kg: 25, datetime: "2025-12-01T09:00:00Z", status: "GENERATED", qr_token: null, assigned_recycler_id: null, certificate: null },
    {
        id: "log-1002",
        institution_id: "inst-001",
        category: "E-Waste",
        weight_kg: 20,
        datetime: "2025-12-01T10:00:00Z",
        status: "VERIFIED",
        qr_token: "demo-token-1002",
        assigned_recycler_id: "rec-001",
        certificate: {
            id: "cert-8f92-4b1a",
            hash: "0x7d5a9b...3f1c",
            block_height: 145023,
            minted_at: "2025-12-01T10:05:00Z",
            authority: "EcoTrace Ledger"
        }
    },
    { id: "log-2001", institution_id: "inst-002", category: "Plastic", weight_kg: 12, datetime: "2025-12-01T08:00:00Z", status: "PENDING_PROOF", qr_token: null, assigned_recycler_id: "rec-002", certificate: null }
  ],
  proofs: [
    { id: "pf-3001", waste_log_id: "log-1002", type: "photo", file_url: "photo-e-waste-1.jpg", uploaded_at: "2025-12-01T10:03:00Z" }
  ]
};

// --- MOCK BACKEND LOGIC ---
const VerificationEngine = {
  verify: (log, proof) => {
    const ocrSuccess = Math.random() > 0.1;
    if (ocrSuccess) {
      return { status: "VERIFIED", score: 92, details: "Timestamp valid. OCR weight match." };
    } else {
      return { status: "PENDING_REVIEW", score: 45, details: "OCR weight mismatch. Manual review needed." };
    }
  },
  signQR: (log) => {
    const payload = btoa(JSON.stringify({ log_id: log.id, w: log.weight_kg }));
    const signature = "hmac_sig_" + Math.random().toString(36).substring(7);
    return `${payload}.${signature}`;
  },
  mintCertificate: (log) => {
      return {
          id: `cert-${Math.random().toString(16).substring(2, 6)}-${Math.random().toString(16).substring(2, 6)}`,
          hash: `0x${Math.random().toString(16).substring(2)}${Math.random().toString(16).substring(2)}`,
          block_height: Math.floor(Math.random() * 500000) + 100000,
          minted_at: new Date().toISOString(),
          authority: "EcoTrace Ledger"
      };
  }
};

// --- COMPONENTS ---

const Notification = ({ message, type, onClose }) => {
  if (!message) return null;
  const styles = {
    success: "bg-emerald-600 text-white",
    error: "bg-red-500 text-white",
    info: "bg-blue-600 text-white"
  };
 
  return (
    <div className={`fixed top-4 right-4 z-[100] px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 animate-fade-in-down ${styles[type] || styles.info}`}>
      {type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
      <span className="font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-80 hover:opacity-100"><X size={16}/></button>
    </div>
  );
};

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-slate-100 p-6 ${className}`}>
    {children}
  </div>
);

const Button = ({ children, variant = "primary", onClick, className = "", disabled=false, type="button" }) => {
  const baseStyle = "px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2";
  const variants = {
    primary: "bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed",
    secondary: "bg-slate-100 text-slate-700 hover:bg-slate-200",
    outline: "border border-slate-200 text-slate-600 hover:border-emerald-500 hover:text-emerald-600 bg-white",
    danger: "bg-red-50 text-red-600 hover:bg-red-100",
    ai: "bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 shadow-sm disabled:opacity-70"
  };
  return (
    <button type={type} onClick={onClick} className={`${baseStyle} ${variants[variant]} ${className}`} disabled={disabled}>
      {children}
    </button>
  );
};

const Badge = ({ status }) => {
  const styles = {
    GENERATED: "bg-slate-100 text-slate-600 border border-slate-200",
    PENDING_PROOF: "bg-amber-50 text-amber-700 border border-amber-100",
    VERIFIED: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    PENDING_REVIEW: "bg-orange-50 text-orange-700 border border-orange-100",
    REJECTED: "bg-red-50 text-red-700 border border-red-100",
    PICKED_UP: "bg-blue-50 text-blue-700 border border-blue-100"
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-bold tracking-wide ${styles[status] || styles.GENERATED}`}>
      {status.replace("_", " ")}
    </span>
  );
};

// --- AI COMPONENTS ---

const SmartSorter = ({ onCategoryFound }) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [analyzingImage, setAnalyzingImage] = useState(false);

  const handleAskAI = async () => {
    if (!query) return;
    setLoading(true);
    setResult(null);
   
    const prompt = `I have a waste item described as: "${query}".
    1. Identify the best category for this item from these options: Food Waste, Plastic, E-Waste, Paper, Metal, Biomedical.
    2. Provide a 1-sentence safe handling instruction.
    Format your response exactly like this:
    Category: [Category Name]
    Tip: [Instruction]`;

    const response = await callGemini(prompt);
    setLoading(false);
   
    const lines = response.split('\n');
    const categoryLine = lines.find(l => l.includes('Category:')) || "Category: Unknown";
    const tipLine = lines.find(l => l.includes('Tip:')) || "Tip: Check local guidelines.";
    const category = categoryLine.replace('Category:', '').trim();
   
    setResult({
      category: category,
      tip: tipLine.replace('Tip:', '').trim()
    });

    if (onCategoryFound && category !== 'Unknown') {
      onCategoryFound(category);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setAnalyzingImage(true);
    setResult(null);
    setQuery("Analyzing image...");

    // Simulate AI Vision API Delay
    await new Promise(r => setTimeout(r, 2000));

    // Mock Response
    const mockVisionResult = {
        category: "Plastic",
        item: "PET Water Bottle",
        tip: "Empty any liquids and crush the bottle to save space before recycling."
    };

    setAnalyzingImage(false);
    setQuery(mockVisionResult.item);
    setResult({
        category: mockVisionResult.category,
        tip: mockVisionResult.tip
    });
   
    if (onCategoryFound) onCategoryFound(mockVisionResult.category);
  };

  return (
    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-indigo-700 font-bold text-sm">
            <Sparkles size={16} /> AI Waste Sorting Assistant
        </div>
        {analyzingImage && <span className="text-xs text-indigo-500 animate-pulse">Processing Image...</span>}
      </div>
     
      <div className="flex gap-2 mb-2">
        <div className="relative flex-1">
            <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Describe item or upload photo..."
            className="w-full p-2 pl-3 pr-10 border border-indigo-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            onKeyDown={(e) => e.key === 'Enter' && handleAskAI()}
            disabled={analyzingImage}
            />
            <button
                onClick={() => document.getElementById('ai-cam-input').click()}
                className="absolute right-2 top-1.5 text-slate-400 hover:text-indigo-600 transition-colors"
                title="Upload Photo"
                disabled={analyzingImage}
            >
                <Camera size={18} />
            </button>
            <input id="ai-cam-input" type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
        </div>
        <Button variant="ai" onClick={handleAskAI} disabled={loading || analyzingImage} className="text-sm py-2 px-3">
          {loading ? "Thinking..." : "Ask"}
        </Button>
      </div>
      {result && (
        <div className="bg-white p-3 rounded-lg border border-indigo-100 text-sm animate-fade-in shadow-sm">
          <div className="font-bold text-slate-800 flex items-center gap-2">
            Recommended: <span className="text-indigo-600">{result.category}</span>
          </div>
          <p className="text-slate-600 mt-1 italic">"{result.tip}"</p>
        </div>
      )}
    </div>
  );
};

const AIAuditAssistant = () => {
    const [status, setStatus] = useState('idle'); // idle, analyzing, done
    const [report, setReport] = useState(null);

    const runAnalysis = async () => {
        setStatus('analyzing');
        await new Promise(r => setTimeout(r, 2500)); // Simulate processing

        // Mock Analysis Result
        setReport({
            score: "A",
            projected_points: "3.8/4.0",
            insights: [
                { type: 'success', text: "Waste segregation rate is above 90% for last 3 months." },
                { type: 'warning', text: "Missing disposal manifests for Biomedical waste in Nov 2025." },
                { type: 'info', text: "E-Waste certificates are up to date. Good job!" }
            ],
            missing_docs: ["Lab Chemical Disposal Log (Nov)", "Canteen Oil Handover Log"]
        });
        setStatus('done');
    };

    return (
        <Card className="mb-8 border-l-4 border-l-indigo-500 shadow-md">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <FileSearch size={20} className="text-indigo-600"/> AI Pre-Audit Gap Analyzer
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">Predict your NAAC/NIRF sustainability score and find missing docs before the auditor arrives.</p>
                </div>
                {status !== 'analyzing' && (
                    <Button variant="ai" onClick={runAnalysis} className="text-sm">
                        {status === 'idle' ? "Run Compliance Check" : "Re-Analyze Data"}
                    </Button>
                )}
            </div>

            {status === 'analyzing' && (
                <div className="py-8 text-center animate-pulse">
                    <div className="inline-block p-4 bg-indigo-50 rounded-full mb-3 text-indigo-600">
                        <RefreshCw size={32} className="animate-spin" />
                    </div>
                    <p className="text-slate-600 font-medium">Scanning waste logs and certificate vault...</p>
                    <p className="text-xs text-slate-400 mt-1">Checking against ISO 14001 & NAAC criteria</p>
                </div>
            )}

            {status === 'done' && report && (
                <div className="animate-fade-in">
                    <div className="grid md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100 col-span-1 text-center">
                            <div className="text-xs font-bold text-indigo-400 uppercase tracking-wider mb-1">Projected Grade</div>
                            <div className="text-4xl font-extrabold text-indigo-700">{report.score}</div>
                            <div className="text-xs text-slate-500 mt-1">Confidence: High</div>
                        </div>
                        <div className="col-span-3 space-y-3">
                            {report.insights.map((insight, idx) => (
                                <div key={idx} className={`p-3 rounded-lg border flex gap-3 items-start text-sm ${
                                    insight.type === 'success' ? 'bg-emerald-50 border-emerald-100 text-emerald-800' :
                                    insight.type === 'warning' ? 'bg-amber-50 border-amber-100 text-amber-800' :
                                    'bg-blue-50 border-blue-100 text-blue-800'
                                }`}>
                                    {insight.type === 'success' ? <CheckCircle size={16} className="mt-0.5 shrink-0"/> :
                                     insight.type === 'warning' ? <AlertTriangle size={16} className="mt-0.5 shrink-0"/> :
                                     <Info size={16} className="mt-0.5 shrink-0"/>}
                                    {insight.text}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <h4 className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
                            <AlertCircle size={16} className="text-red-500"/> Action Required: Missing Documentation
                        </h4>
                        <div className="flex flex-wrap gap-2">
                            {report.missing_docs.map((doc, i) => (
                                <span key={i} className="px-3 py-1 bg-white border border-slate-200 rounded-full text-xs font-medium text-slate-600 shadow-sm flex items-center gap-2">
                                    {doc} <button className="text-indigo-600 hover:text-indigo-800"><Upload size={12}/></button>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
};

const DashboardInsights = ({ logs }) => {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateInsights = async () => {
    setLoading(true);
    // Simple stats for context
    const totalWeight = logs.reduce((acc, curr) => acc + curr.weight_kg, 0);
    const categories = [...new Set(logs.map(l => l.category))].join(", ");
   
    const prompt = `I am an institution managing waste. Here is my recent data:
    - Total Waste Logged: ${totalWeight} kg
    - Categories: ${categories}
    - Recent Log Count: ${logs.length}
   
    Please provide:
    1. A 2-sentence encouraging summary of my performance.
    2. One specific, actionable sustainability tip relevant to schools/colleges.
   
    Keep the tone professional yet motivating. Use emoji.`;

    const response = await callGemini(prompt);
    setInsight(response);
    setLoading(false);
  };

  return (
    <Card className="bg-gradient-to-br from-indigo-900 to-slate-900 text-white border-none relative overflow-hidden shadow-lg">
      <div className="absolute top-0 right-0 p-4 opacity-10"><Sparkles size={100} /></div>
      <div className="relative z-10">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-2 text-indigo-200 font-bold uppercase text-xs tracking-wider">
            <Lightbulb size={16} /> AI Eco-Consultant
          </div>
          {!insight && (
            <Button variant="ai" onClick={generateInsights} disabled={loading} className="text-xs py-1 px-3 h-8 shadow-none border border-white/20">
              {loading ? "Analyzing..." : "Generate Insights"}
            </Button>
          )}
        </div>
       
        {insight ? (
          <div className="animate-fade-in">
            <p className="text-indigo-100 text-sm leading-relaxed whitespace-pre-line">{insight}</p>
            <button onClick={() => setInsight(null)} className="text-indigo-300 text-xs mt-3 hover:text-white underline">Refresh</button>
          </div>
        ) : (
          <p className="text-indigo-200/60 text-sm italic">
            Click generate to analyze your waste footprint and get personalized sustainability tips powered by Gemini.
          </p>
        )}
      </div>
    </Card>
  );
};

const DigitalCertificate = ({ log, institution, onClose }) => {
    if(!log || !log.certificate) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white w-full max-w-3xl rounded-xl shadow-2xl overflow-hidden relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 z-10">
                    <X size={24}/>
                </button>

                {/* Certificate Border Design */}
                <div className="p-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500">
                    <div className="bg-white p-8 md:p-12 relative overflow-hidden">
                        {/* Background Watermark */}
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none">
                            <Award size={400}/>
                        </div>

                        {/* Header */}
                        <div className="text-center mb-10">
                            <div className="flex justify-center mb-4 text-emerald-600">
                                <Award size={48} strokeWidth={1.5} />
                            </div>
                            <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-800 tracking-wide mb-2">CERTIFICATE OF RECYCLING</h2>
                            <p className="text-slate-500 uppercase tracking-widest text-xs font-bold">EcoTrace Digital Ledger • Immutable Record</p>
                        </div>

                        {/* Content */}
                        <div className="grid md:grid-cols-2 gap-8 mb-10 relative z-10">
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Issued To</label>
                                    <div className="text-lg font-bold text-slate-800">{institution.name}</div>
                                    <div className="text-sm text-slate-500">{institution.city}</div>
                                </div>
                                <div>
                                    <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Material Details</label>
                                    <div className="flex items-center gap-3">
                                        <div className="text-2xl font-bold text-emerald-600">{log.weight_kg} kg</div>
                                        <span className="text-slate-300">|</span>
                                        <div className="text-lg text-slate-700">{log.category}</div>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Date of Generation</label>
                                    <div className="text-slate-700 font-medium">{new Date(log.datetime).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                                </div>
                            </div>
                           
                            <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                                <h4 className="flex items-center gap-2 font-bold text-slate-700 mb-4 border-b border-slate-200 pb-2">
                                    <ShieldCheck size={18} className="text-emerald-500"/> Digital Verification
                                </h4>
                                <div className="space-y-4 text-sm font-mono">
                                    <div>
                                        <span className="text-slate-400 block text-xs mb-0.5">Certificate ID</span>
                                        <span className="text-slate-700 break-all">{log.certificate.id}</span>
                                    </div>
                                    <div>
                                        <span className="text-slate-400 block text-xs mb-0.5">Cryptographic Hash</span>
                                        <span className="text-slate-600 break-all text-xs leading-tight">{log.certificate.hash}</span>
                                    </div>
                                    <div className="flex justify-between items-center pt-2">
                                        <div>
                                            <span className="text-slate-400 block text-xs mb-0.5">Block Height</span>
                                            <span className="text-indigo-600 font-bold">#{log.certificate.block_height}</span>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-slate-400 block text-xs mb-0.5">Authority</span>
                                            <span className="text-emerald-700 font-bold">{log.certificate.authority}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 border-t border-slate-100">
                            <div className="flex gap-4">
                                <Button variant="outline" className="text-xs" onClick={() => alert("Simulating PDF Download...")}>
                                    <Download size={14}/> Download PDF
                                </Button>
                                <Button variant="outline" className="text-xs" onClick={() => alert("Public Link Copied!")}>
                                    <Share2 size={14}/> Share Link
                                </Button>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] text-slate-400 mb-1">Signed by Verification Engine v2.0</div>
                                <div className="font-serif italic text-slate-600">EcoTrace Automated System</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const EPRCalculator = () => {
    const [calculating, setCalculating] = useState(false);
    const [result, setResult] = useState(null);
    const [formData, setFormData] = useState({ category: 'Plastic (Rigid)', weight: '' });
 
    // Mock Market Data (In a real app, this comes from an API)
    const marketRates = {
      'Plastic (Rigid)': { rate: 2.8, unit: '₹/kg', trend: 'up', demand: 'High' },
      'Plastic (Flexible)': { rate: 1.5, unit: '₹/kg', trend: 'down', demand: 'Moderate' },
      'E-Waste (PCB)': { rate: 45.0, unit: '₹/kg', trend: 'up', demand: 'Very High' },
      'E-Waste (General)': { rate: 12.0, unit: '₹/kg', trend: 'stable', demand: 'High' },
      'Batteries (Li-ion)': { rate: 22.5, unit: '₹/kg', trend: 'up', demand: 'Critical' },
    };
 
    const handleCalculate = async () => {
      if (!formData.weight) return;
      setCalculating(true);
      setResult(null);
 
      // Simulate AI Processing Delay
      await new Promise(r => setTimeout(r, 1500));
 
      const rateInfo = marketRates[formData.category] || { rate: 0, unit: '₹/kg', trend: 'flat' };
      const estimatedCredits = (parseFloat(formData.weight) * rateInfo.rate).toFixed(2);
      const processingFee = (estimatedCredits * 0.1).toFixed(2); // 10% platform fee
      const netRevenue = (estimatedCredits - processingFee).toFixed(2);
 
      setResult({
        credits: estimatedCredits,
        fee: processingFee,
        net: netRevenue,
        rate: rateInfo.rate,
        trend: rateInfo.trend,
        analysis: generateMockAnalysis(formData.category, rateInfo.trend)
      });
      setCalculating(false);
    };
 
    const generateMockAnalysis = (cat, trend) => {
      const trends = {
        'up': "Market demand is peaking due to new recycled content mandates in manufacturing.",
        'down': "Oversupply in the local market is causing a slight dip in credit value.",
        'stable': "Prices are stable. Consistent demand from authorized recyclers."
      };
      return `AI Analysis: ${cat} credits are currently ${trend === 'up' ? 'bullish' : 'bearish'}. ${trends[trend] || ''}`;
    };
 
    return (
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <div className="flex items-center gap-2 mb-6 text-emerald-700 font-bold text-lg">
              <Calculator size={20} /> Credit Calculator
            </div>
           
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Material Category</label>
                <select
                  className="w-full p-3 border border-slate-200 rounded-lg bg-slate-50 font-medium text-slate-700 outline-none focus:ring-2 focus:ring-emerald-500"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                >
                  {Object.keys(marketRates).map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Processed Weight (kg)</label>
                <input
                  type="number"
                  className="w-full p-3 border border-slate-200 rounded-lg bg-slate-50 font-medium text-slate-700 outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="e.g. 500"
                  value={formData.weight}
                  onChange={(e) => setFormData({...formData, weight: e.target.value})}
                />
              </div>
            </div>
 
            <Button
              onClick={handleCalculate}
              disabled={calculating || !formData.weight}
              className="w-full py-3 text-lg shadow-lg shadow-emerald-100"
              variant="ai"
            >
              {calculating ? (
                <span className="flex items-center gap-2">
                  <Sparkles size={18} className="animate-spin" /> Analyzing Market Rates...
                </span>
              ) : (
                "Calculate Potential Credits"
              )}
            </Button>
 
            {result && (
              <div className="mt-8 animate-fade-in">
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-100">
                  <div className="flex justify-between items-end mb-4 border-b border-emerald-200/50 pb-4">
                    <div>
                      <div className="text-sm text-emerald-600 font-medium mb-1">Gross Credit Value</div>
                      <div className="text-3xl font-bold text-emerald-800">₹{result.credits}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-slate-500 mb-1">Net Revenue (Est.)</div>
                      <div className="text-xl font-bold text-slate-700">₹{result.net}</div>
                    </div>
                  </div>
                 
                  <div className="bg-white/60 rounded-lg p-3 text-sm text-emerald-800 flex gap-3 items-start">
                    <Sparkles size={16} className="shrink-0 mt-0.5 text-indigo-500" />
                    {result.analysis}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
 
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-slate-900 rounded-xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold flex items-center gap-2"><TrendingUp size={18} className="text-emerald-400"/> Live Market Rates</h3>
              <span className="text-[10px] bg-slate-800 px-2 py-1 rounded text-slate-400">UPDATED TODAY</span>
            </div>
           
            <div className="space-y-4">
              {Object.entries(marketRates).map(([cat, data]) => (
                <div key={cat} className="flex justify-between items-center text-sm border-b border-slate-800 pb-3 last:border-0">
                  <div>
                    <div className="font-medium text-slate-200">{cat}</div>
                    <div className="text-xs text-slate-500">Demand: <span className={data.demand === 'Very High' || data.demand === 'Critical' ? 'text-emerald-400' : 'text-slate-400'}>{data.demand}</span></div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-bold text-emerald-400">₹{data.rate}</div>
                    <div className="text-xs flex items-center justify-end gap-1 text-slate-500">
                      {data.trend === 'up' ? <TrendingUp size={10} className="text-emerald-500"/> : data.trend === 'down' ? <TrendingDown size={10} className="text-red-500"/> : <span>-</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
 
          <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100 text-sm text-indigo-800">
            <h4 className="font-bold flex items-center gap-2 mb-2"><Info size={16}/> How is this calculated?</h4>
            <p className="opacity-80 leading-relaxed">
              Credits are calculated based on the <strong>weight</strong> of recycled material multiplied by the <strong>base EPR rate</strong> set by the CPCB, adjusted for current market demand and processing efficiency.
            </p>
          </div>
        </div>
      </div>
    );
  };

// --- APP SHELL ---

export default function App() {
  const [view, setView] = useState("landing");
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notification, setNotification] = useState(null);
 
  // Database State
  const [logs, setLogs] = useState(SEED_DATA.waste_logs);
  const [proofs, setProofs] = useState(SEED_DATA.proofs);
  const [selectedCertificateLog, setSelectedCertificateLog] = useState(null);
 
  // Helpers
  const activeLogs = logs.filter(l => l.institution_id === user?.id);
  const pendingLogs = activeLogs.filter(l => l.status === "PENDING_PROOF");

  const notify = (msg, type='success') => {
    setNotification({ message: msg, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const login = (role, id) => {
    const entity = role === 'institution'
      ? SEED_DATA.institutions.find(i => i.id === id)
      : SEED_DATA.recyclers.find(r => r.id === id);
   
    setUser({ ...entity, role });
    setView(role === 'institution' ? 'inst-dashboard' : 'rec-dashboard');
    window.scrollTo(0,0);
  };

  const logout = () => {
    setUser(null);
    setView('landing');
    setMobileMenuOpen(false);
    window.scrollTo(0,0);
  };

  const handleLogWaste = (newLog) => {
    const logEntry = {
      id: `log-${Date.now()}`,
      institution_id: user.id,
      datetime: new Date().toISOString(),
      status: "GENERATED",
      assigned_recycler_id: null,
      qr_token: null,
      certificate: null,
      ...newLog
    };
    setLogs([logEntry, ...logs]);
    return logEntry;
  };

  const handleUploadProof = (logId, file) => {
    const newProof = {
      id: `pf-${Date.now()}`,
      waste_log_id: logId,
      type: "photo",
      file_url: URL.createObjectURL(file),
      uploaded_at: new Date().toISOString()
    };
    setProofs([...proofs, newProof]);
    const log = logs.find(l => l.id === logId);
    const result = VerificationEngine.verify(log, newProof);
   
    // Auto-mint certificate if verified
    let certData = null;
    if(result.status === "VERIFIED") {
        certData = VerificationEngine.mintCertificate(log);
    }

    const updatedLogs = logs.map(l =>
      l.id === logId ? { ...l, status: result.status, certificate: certData } : l
    );
    setLogs(updatedLogs);
    return result;
  };

  const generateQR = (logId) => {
    const log = logs.find(l => l.id === logId);
    const token = VerificationEngine.signQR(log);
    const updatedLogs = logs.map(l =>
      l.id === logId ? { ...l, qr_token: token } : l
    );
    setLogs(updatedLogs);
    return token;
  };

  const handleRecyclerScan = (token) => {
    const validLog = logs.find(l => l.qr_token === token);
    if (validLog) {
      const updatedLogs = logs.map(l =>
        l.id === validLog.id ? { ...l, status: "PICKED_UP" } : l
      );
      setLogs(updatedLogs);
      return { success: true, log: validLog };
    }
    return { success: false };
  };

  // --- LANDING VIEW ---
  if (view === "landing") {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col font-sans text-slate-900">
        <header className="bg-white p-6 shadow-sm sticky top-0 z-50 flex justify-between items-center border-b border-slate-100">
          <div className="flex items-center gap-2 text-emerald-700 font-bold text-xl">
            <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center text-white shadow-sm">
              <LeafIcon />
            </div>
            EcoTrace
          </div>
          <div className="flex gap-6">
            <button onClick={() => document.getElementById('about-section').scrollIntoView({behavior: 'smooth'})} className="text-slate-600 font-medium hidden md:block hover:text-emerald-600 transition-colors">About Platform</button>
            <button onClick={() => document.getElementById('credits-section').scrollIntoView({behavior: 'smooth'})} className="text-slate-600 font-medium hidden md:block hover:text-emerald-600 transition-colors">Credits</button>
          </div>
        </header>
       
        <div className="bg-white pb-16 pt-12 px-6 border-b border-slate-100">
          <main className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 text-xs font-bold uppercase tracking-wide mb-6 border border-emerald-100">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> India's Environmental OS
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 mb-6 leading-tight tracking-tight">
              Unified Compliance, Waste, & <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">Credit Infrastructure</span>
            </h1>
            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              One platform for Institutions to manage waste and for Recyclers to source feedstock. Verify compliance, generate EPR credits, and prepare for Carbon Markets.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 w-full justify-center mb-12">
               <Button className="py-4 px-8 text-lg shadow-xl shadow-emerald-200 hover:shadow-2xl hover:-translate-y-1 transition-all" onClick={() => login('institution', 'inst-001')}>
                 Start Demo as Institution
                 <span className="text-xs opacity-75 block font-normal ml-2">(St. Xavier College)</span>
               </Button>
               <Button variant="outline" className="py-4 px-8 text-lg shadow-sm hover:shadow-md transition-all" onClick={() => login('recycler', 'rec-001')}>
                 Start Demo as Recycler
                 <span className="text-xs block font-normal text-slate-400 ml-2">(Urban E-Cycle Hub)</span>
               </Button>
            </div>
          </main>
        </div>

        {/* About Section */}
        <div id="about-section" className="py-20 px-6 max-w-6xl mx-auto w-full">
           <h2 className="text-3xl font-bold text-slate-800 mb-12 text-center">Unified Ecosystem</h2>
           <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-t-4 border-t-emerald-500 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="mb-6 p-4 bg-emerald-50 text-emerald-600 rounded-2xl w-fit">
                <Building2 size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Educational Campuses</h3>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-4">Schools • Colleges</p>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex gap-2"><CheckCircle size={16} className="text-emerald-500 shrink-0 mt-0.5" /> Automate Green Audits & Reporting.</li>
                <li className="flex gap-2"><CheckCircle size={16} className="text-emerald-500 shrink-0 mt-0.5" /> Monetize recyclable campus waste.</li>
                <li className="flex gap-2 font-medium text-emerald-700 bg-emerald-50 p-2 rounded-lg"><CheckCircle size={16} className="text-emerald-600 shrink-0 mt-0.5" /> Boost NAAC & NIRF Sustainability Scores.</li>
              </ul>
            </Card>

            <Card className="border-t-4 border-t-blue-500 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="mb-6 p-4 bg-blue-50 text-blue-600 rounded-2xl w-fit">
                <Stethoscope size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Healthcare Facilities</h3>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-4">Hospitals • Clinics</p>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex gap-2"><CheckCircle size={16} className="text-blue-500 shrink-0 mt-0.5" /> Track Bio-Medical waste disposal.</li>
                <li className="flex gap-2"><CheckCircle size={16} className="text-blue-500 shrink-0 mt-0.5" /> Infection control compliance logs.</li>
                <li className="flex gap-2 font-medium text-blue-700 bg-blue-50 p-2 rounded-lg"><CheckCircle size={16} className="text-blue-600 shrink-0 mt-0.5" /> NABH & JCI Environmental Compliance.</li>
              </ul>
            </Card>

            <Card className="border-t-4 border-t-purple-500 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="mb-6 p-4 bg-purple-50 text-purple-600 rounded-2xl w-fit">
                <Factory size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Processing Partners</h3>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-4">Recyclers • Refurbishers</p>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex gap-2"><CheckCircle size={16} className="text-purple-500 shrink-0 mt-0.5" /> Source verified bulk feedstock.</li>
                <li className="flex gap-2"><CheckCircle size={16} className="text-purple-500 shrink-0 mt-0.5" /> EPR Credit issuance engine.</li>
                <li className="flex gap-2 font-medium text-purple-700 bg-purple-50 p-2 rounded-lg"><CheckCircle size={16} className="text-purple-600 shrink-0 mt-0.5" /> CPCB/SPCB & ISO Audit Readiness.</li>
              </ul>
            </Card>
           </div>
        </div>

        {/* Credits Section */}
        <div id="credits-section" className="bg-slate-50 py-20 px-6 border-t border-slate-200">
           <div className="max-w-5xl mx-auto">
             <div className="text-center mb-12">
               <h2 className="text-3xl font-bold text-slate-800 mb-3">Explore Credit Opportunities</h2>
               <p className="text-slate-500 text-lg">Monetize your waste and sustainability efforts.</p>
             </div>

             <div className="grid md:grid-cols-2 gap-8">
               <div className="bg-white rounded-3xl p-8 border border-emerald-100 relative overflow-hidden group hover:shadow-xl transition-all shadow-sm">
                 <div className="absolute top-0 right-0 bg-emerald-100 text-emerald-800 text-xs font-bold px-4 py-2 rounded-bl-2xl">LIVE NOW</div>
                 <div className="mb-6 text-emerald-600 bg-emerald-50 w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner">
                   <FileCheck size={28} />
                 </div>
                 <h3 className="text-2xl font-bold text-slate-900 mb-3">EPR Credits</h3>
                 <p className="text-slate-600 mb-8 text-base leading-relaxed">
                   Earn revenue by generating Extended Producer Responsibility credits for Plastic, E-Waste, and Batteries. Our platform connects you directly with producers.
                 </p>
                 <button className="text-emerald-700 font-bold text-sm flex items-center gap-2 group-hover:gap-3 transition-all bg-emerald-50 px-4 py-2 rounded-lg hover:bg-emerald-100">
                   Check Market Rates <ArrowRight size={16} />
                 </button>
               </div>
               <div className="bg-slate-100 rounded-3xl p-8 border border-slate-200 relative overflow-hidden group hover:shadow-xl transition-all">
                 <div className="absolute top-0 right-0 bg-purple-100 text-purple-700 text-xs font-bold px-4 py-2 rounded-bl-2xl">COMING 2026</div>
                 <div className="mb-6 text-purple-600 bg-white w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm">
                   <Globe size={28} />
                 </div>
                 <h3 className="text-2xl font-bold text-slate-900 mb-3">Carbon Credits</h3>
                 <p className="text-slate-600 mb-8 text-base leading-relaxed">
                   Prepare for India's CCTS market. Start building your data baseline today. We automate the calculation of Scope 3 emission reductions from waste diversion.
                 </p>
                 <button className="text-purple-700 font-bold text-sm flex items-center gap-2 group-hover:gap-3 transition-all bg-white px-4 py-2 rounded-lg hover:bg-purple-50">
                   View Roadmap <ArrowRight size={16} />
                 </button>
               </div>
             </div>
           </div>
        </div>
       
        <footer className="bg-slate-900 text-slate-400 py-12 text-center text-sm">
          <div className="flex items-center justify-center gap-2 mb-4 text-slate-200 font-bold text-lg">
            <div className="w-6 h-6 bg-emerald-600 rounded flex items-center justify-center text-white text-xs">
              <LeafIcon size={14} />
            </div>
            EcoTrace
          </div>
          <p>Prototype Build v0.3.1 • Designed for Pitch</p>
        </footer>
      </div>
    );
  }

  // --- DASHBOARD LAYOUT ---
  const SidebarItem = ({ icon: Icon, label, active, onClick }) => (
    <button
      onClick={() => { onClick(); setMobileMenuOpen(false); }}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${active ? 'bg-emerald-600 text-white shadow-md' : 'text-slate-300 hover:bg-slate-800'}`}
    >
      <Icon size={20} />
      <span className="font-medium">{label}</span>
    </button>
  );

  const DashboardLayout = ({ children }) => (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {notification && <Notification {...notification} onClose={() => setNotification(null)} />}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-slate-900 text-white transform transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0 shadow-xl md:shadow-none`}>
        <div className="p-6 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center gap-2 font-bold text-xl">
             <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-900/50">
               <LeafIcon size={16} />
             </div>
             EcoTrace
          </div>
          <button onClick={() => setMobileMenuOpen(false)} className="md:hidden text-slate-400 hover:text-white"><X /></button>
        </div>
       
        <nav className="px-4 space-y-2 mt-6">
          <div className="mb-4 px-2 text-xs font-bold text-slate-500 uppercase tracking-wider">Menu</div>
          {user.role === 'institution' ? (
            <>
              <SidebarItem icon={LayoutDashboard} label="Dashboard" active={view === 'inst-dashboard'} onClick={() => setView('inst-dashboard')} />
              <SidebarItem icon={FileText} label="Evidence Vault" active={view === 'vault'} onClick={() => setView('vault')} />
              <SidebarItem icon={FileCheck} label="Audit Reports" active={view === 'audit'} onClick={() => setView('audit')} />
              <SidebarItem icon={Users} label="Assign Partners" active={view === 'partners'} onClick={() => setView('partners')} />
            </>
          ) : (
            <>
              <SidebarItem icon={LayoutDashboard} label="Pickups" active={view === 'rec-dashboard'} onClick={() => setView('rec-dashboard')} />
              <SidebarItem icon={ScanLine} label="Scan QR" active={view === 'scan'} onClick={() => setView('scan')} />
              <SidebarItem icon={Calculator} label="Credit Calc" active={view === 'credit-calculator'} onClick={() => setView('credit-calculator')} />
            </>
          )}
          <div className="pt-8 mt-8 border-t border-slate-800">
             <SidebarItem icon={LogOut} label="Log Out" onClick={logout} />
          </div>
        </nav>
      </aside>

      <div className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50">
        <header className="bg-white border-b border-slate-200 p-4 flex justify-between items-center lg:px-8 shadow-sm">
          <button onClick={() => setMobileMenuOpen(true)} className="md:hidden text-slate-600 hover:text-slate-900"><Menu /></button>
          <div className="font-semibold text-slate-700 flex flex-col">
            <span className="text-lg">{user.name}</span>
            {user.role === 'institution' && <span className="text-xs font-normal text-slate-500">{user.city} Campus • ID: <span className="font-mono">{user.id}</span></span>}
          </div>
          <div className="h-10 w-10 bg-gradient-to-br from-emerald-100 to-emerald-200 text-emerald-700 rounded-full flex items-center justify-center font-bold text-lg border border-emerald-100 shadow-sm">
            {user.name[0]}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-8 scroll-smooth">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* GLOBAL MODALS */}
      {selectedCertificateLog && (
        <DigitalCertificate
            log={selectedCertificateLog}
            institution={user}
            onClose={() => setSelectedCertificateLog(null)}
        />
      )}
    </div>
  );

  // --- INSTITUTION DASHBOARD VIEWS ---

  if (view === 'inst-dashboard') {
    return (
      <DashboardLayout>
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-800">Dashboard Overview</h2>
          <p className="text-slate-500">Track your waste metrics and compliance status.</p>
        </div>

        {/* AI INSIGHTS CARD */}
        <div className="mb-8">
          <DashboardInsights logs={activeLogs} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <MetricCard label="Waste Generated" value="57 kg" sub="This Month" icon={Trash2} color="blue" />
          <MetricCard label="Waste Processed" value="32 kg" sub="Verified Recycled" icon={CheckCircle} color="emerald" />
          <MetricCard label="Pending Proofs" value={pendingLogs.length} sub="Action Required" icon={AlertCircle} color="amber" />
        </div>

        {/* LOG WASTE CARD */}
        <div className="mb-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg"><Plus size={20}/></div>
                <h3 className="text-xl font-bold text-slate-800">Log Waste Generation</h3>
              </div>
             
              {/* AI SORTER */}
              <SmartSorter onCategoryFound={(cat) => {
                const select = document.getElementById('category-select');
                if (select) {
                    select.value = cat;
                    // Trigger simple animation or highlight
                    select.focus();
                }
              }} />

              <form onSubmit={(e) => {
                e.preventDefault();
                const form = e.target;
                handleLogWaste({
                  category: form.category.value,
                  weight_kg: Number(form.weight.value)
                });
                form.reset();
                notify("Waste logged successfully! Please upload proof.", "success");
              }} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Waste Category</label>
                      <div className="relative">
                          <select id="category-select" name="category" className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none bg-slate-50 appearance-none">
                            <option>Food Waste</option>
                            <option>Plastic</option>
                            <option>E-Waste</option>
                            <option>Paper</option>
                            <option>Metal</option>
                            <option>Biomedical</option>
                          </select>
                          <div className="absolute right-3 top-3.5 text-slate-400 pointer-events-none"><Menu size={16}/></div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Weight Generated (kg)</label>
                      <input name="weight" type="number" step="0.1" className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none bg-slate-50" placeholder="0.00" required />
                    </div>
                </div>
               
                <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-slate-100 mt-4">
                  <Button type="submit" className="flex-1 justify-center bg-emerald-600 hover:bg-emerald-700 shadow-md shadow-emerald-200">
                     <CheckCircle size={18} /> Log Entry
                  </Button>
                  <button type="button" onClick={() => {
                     const pending = activeLogs.find(l => l.status === "PENDING_PROOF");
                     if (pending) document.getElementById('proof-upload-input').click();
                     else notify("Please log waste first before uploading proof.", "error");
                  }} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                     <Upload size={18} /> Upload Proof
                  </button>
                  {/* Hidden File Input for Button */}
                  <input id="proof-upload-input" type="file" className="hidden" onChange={(e) => {
                     const pending = activeLogs.find(l => l.status === "PENDING_PROOF");
                     if(pending && e.target.files[0]) {
                       const res = handleUploadProof(pending.id, e.target.files[0]);
                       notify(`Proof uploaded! Status: ${res.status}`, res.status === 'VERIFIED' ? 'success' : 'info');
                     }
                  }} />

                  <button type="button" onClick={() => {
                     const verified = activeLogs.find(l => l.status === "VERIFIED" && !l.qr_token);
                     if (verified) {
                       generateQR(verified.id);
                       notify("QR Code Generated for the verified log!", "success");
                     }
                     else notify("No verified logs waiting for QR generation. Upload proof first.", "error");
                  }} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-slate-300 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                     <QrCode size={18} /> Generate QR
                  </button>
                </div>
              </form>
            </Card>
          </div>
         
          <div className="lg:col-span-1 space-y-6">
              <div className="bg-emerald-600 rounded-xl p-6 text-white shadow-lg shadow-emerald-200">
                  <div className="flex items-center gap-2 mb-4 opacity-90"><TrendingUp size={20}/> Compliance Score</div>
                  <div className="text-4xl font-bold mb-2">94%</div>
                  <div className="w-full bg-white/20 h-2 rounded-full overflow-hidden">
                      <div className="bg-white h-full w-[94%]"></div>
                  </div>
                  <p className="text-sm mt-3 text-emerald-100">Excellent. You are on track for a top-tier Green Audit rating.</p>
              </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-slate-50 font-bold text-slate-700 flex justify-between items-center">
            <span>Recent Waste Generation Logs</span>
            <Button variant="outline" className="text-xs py-1 px-3 h-8">Export CSV</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left whitespace-nowrap">
              <thead className="text-slate-500 font-medium border-b border-slate-100 bg-slate-50/50">
                <tr>
                  <th className="p-4">Date</th>
                  <th className="p-4">Category</th>
                  <th className="p-4">Weight</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {activeLogs.length === 0 ? (
                  <tr><td colSpan="5" className="p-8 text-center text-slate-500 italic">No waste logs yet. Start logging above.</td></tr>
                ) : (
                  activeLogs.map(log => (
                    <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                      <td className="p-4 text-slate-500">{new Date(log.datetime).toLocaleDateString()}</td>
                      <td className="p-4 font-medium text-slate-700">{log.category}</td>
                      <td className="p-4">{log.weight_kg} kg</td>
                      <td className="p-4"><Badge status={log.status} /></td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                             {/* Certificate Action */}
                            {log.certificate ? (
                                <button onClick={() => setSelectedCertificateLog(log)} className="inline-flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded border border-indigo-100 font-medium hover:bg-indigo-100 transition-colors" title="View Digital Certificate">
                                    <Award size={14}/> Cert
                                </button>
                            ) : null}

                            {log.qr_token ? (
                                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded border border-emerald-100 font-mono"><QrCode size={12}/> QR Ready</span>
                            ) : (
                                <span className="text-slate-300">-</span>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Reuse other views (Vault, Audit, etc.)
  if (view === 'vault') {
    return (
      <DashboardLayout>
        <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-800">Evidence Vault</h2>
            <p className="text-slate-500">Secure storage for compliance documentation.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
           <FolderCard title="Waste Proofs" count={proofs.length} color="bg-blue-100 text-blue-700" />
           <FolderCard title="MoUs" count={1} color="bg-purple-100 text-purple-700" />
           <FolderCard title="Certificates" count={activeLogs.filter(l => l.certificate).length} color="bg-amber-100 text-amber-700" />
        </div>
      </DashboardLayout>
    );
  }

  if (view === 'audit') {
    return (
      <DashboardLayout>
         <div className="max-w-2xl mx-auto text-center pt-10">
            <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6 text-emerald-600">
                <FileCheck size={40} />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Green Audit Report</h2>
            <p className="text-slate-600 mb-8">Generate a compliant audit report for NAAC/NIRF submissions.</p>
           
            <AIAuditAssistant />

            <Card className="text-left max-w-lg mx-auto mb-8 shadow-lg">
               <div className="space-y-4">
                 <div>
                   <label className="text-sm font-bold text-slate-700 mb-1 block">Audit Period</label>
                   <select className="w-full mt-1 p-3 border rounded-lg bg-slate-50"><option>Last 30 Days</option><option>Last 6 Months</option></select>
                 </div>
                 <div className="bg-blue-50 p-3 rounded-lg flex gap-3 items-start text-xs text-blue-700">
                    <AlertCircle size={16} className="shrink-0 mt-0.5"/>
                    This report includes all verified waste logs and digital recycling certificates for the selected period.
                 </div>
               </div>
               <Button className="w-full mt-6 justify-center" onClick={() => notify("Generating PDF Report...", "info")}>
                 Download PDF Report
               </Button>
            </Card>
         </div>
      </DashboardLayout>
    )
  }

  if (view === 'partners') {
    return (
      <DashboardLayout>
        <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-800">Assign Recyclers</h2>
            <p className="text-slate-500">Manage your waste pickup partners.</p>
        </div>
        <div className="grid gap-6">
          {SEED_DATA.recyclers.map(r => (
            <Card key={r.id} className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:shadow-md transition-shadow">
               <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-600">
                      {r.name[0]}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-800">{r.name}</h3>
                    <div className="flex gap-2 mt-1 flex-wrap">
                        {r.categories.map(c => <span key={c} className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-600 border border-slate-200">{c}</span>)}
                    </div>
                  </div>
               </div>
               <div className="flex gap-3 w-full md:w-auto">
                   <Button variant="outline" className="flex-1 md:flex-none">View Profile</Button>
                   <Button className="flex-1 md:flex-none">Assign</Button>
               </div>
            </Card>
          ))}
        </div>
      </DashboardLayout>
    )
  }

  // --- RECYCLER DASHBOARD VIEWS ---

  if (view === 'rec-dashboard') {
    const assignedLogs = logs.filter(l => l.assigned_recycler_id === user.id);
    return (
      <DashboardLayout>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
             <div>
                <h2 className="text-2xl font-bold text-slate-800">Recycler Dashboard</h2>
                <p className="text-slate-500">Manage pickups and credits.</p>
             </div>
             <Button onClick={() => setView('scan')} className="shadow-lg shadow-emerald-200"><ScanLine size={18} /> Scan QR for Pickup</Button>
          </div>

          {/* RECYCLER CREDIT OPPORTUNITIES */}
          <h3 className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2"><Sparkles size={18} className="text-amber-500"/> Monetization & Credits</h3>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* EPR Credits Card */}
            <div
                onClick={() => setView('credit-calculator')}
                className="bg-emerald-50 rounded-xl p-6 border border-emerald-100 relative overflow-hidden flex flex-col justify-between h-40 group hover:border-emerald-200 hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer"
            >
              <div className="absolute top-0 right-0 bg-emerald-200 text-emerald-800 text-[10px] font-bold px-2 py-1 rounded-bl-lg">LIVE NOW</div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg text-emerald-600 shadow-sm"><FileCheck size={20} /></div>
                <h3 className="font-bold text-slate-900">EPR Credits</h3>
              </div>
              <p className="text-xs text-slate-600 mt-2">Generate tradeable Extended Producer Responsibility credits for collected waste.</p>
              <button className="text-emerald-700 font-bold text-xs flex items-center gap-1 mt-3 group-hover:gap-2 transition-all">
                Access Calculator <ArrowRight size={14} />
              </button>
            </div>

            {/* Carbon Credits Card */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 relative overflow-hidden flex flex-col justify-between h-40 opacity-75 hover:opacity-100 transition-opacity cursor-pointer">
              <div className="absolute top-0 right-0 bg-purple-100 text-purple-700 text-[10px] font-bold px-2 py-1 rounded-bl-lg">COMING 2026</div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg text-purple-600 shadow-sm"><Globe size={20} /></div>
                <h3 className="font-bold text-slate-900">Carbon Credits</h3>
              </div>
              <p className="text-xs text-slate-600 mt-2">Future-ready: Track Scope 3 emissions avoided by your recycling operations.</p>
              <button className="text-purple-700 font-bold text-xs flex items-center gap-1 mt-3 hover:gap-2 transition-all">
                Join Waitlist <ArrowRight size={14} />
              </button>
            </div>
          </div>

          <h3 className="text-lg font-bold text-slate-700 mb-4">Assigned Pickups</h3>
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            {assignedLogs.length === 0 ? (
               <div className="p-12 text-center text-slate-500 flex flex-col items-center gap-3">
                   <div className="p-3 bg-slate-50 rounded-full"><Trash2 size={24}/></div>
                   No pickups assigned yet.
               </div>
            ) : (
               assignedLogs.map(log => (
                 <div key={log.id} className="p-4 border-b border-slate-100 flex justify-between items-center hover:bg-slate-50 transition-colors">
                   <div className="flex items-center gap-4">
                      <div className="p-3 bg-slate-100 rounded-lg text-slate-500">
                          <Trash2 size={20} />
                      </div>
                      <div>
                        <div className="font-bold text-slate-800 text-lg">{log.weight_kg} kg <span className="text-slate-400 font-normal">of</span> {log.category}</div>
                        <div className="text-sm text-slate-500 flex items-center gap-1"><Building2 size={12}/> {SEED_DATA.institutions.find(i => i.id === log.institution_id)?.name}</div>
                      </div>
                   </div>
                   <Badge status={log.status} />
                 </div>
               ))
            )}
          </div>
      </DashboardLayout>
    )
  }

  // --- NEW: CREDIT CALCULATOR VIEW ---
  if (view === 'credit-calculator') {
    return (
        <DashboardLayout>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
               <div>
                  <h2 className="text-2xl font-bold text-slate-800">EPR Credit Estimator</h2>
                  <p className="text-slate-500">Calculate projected revenue from waste certificates.</p>
               </div>
               <Button onClick={() => setView('rec-dashboard')} variant="outline"><ArrowRight className="rotate-180" size={16}/> Back to Dashboard</Button>
            </div>
           
            <EPRCalculator />
        </DashboardLayout>
    )
  }

  if (view === 'scan') {
    return (
      <DashboardLayout>
        <div className="max-w-md mx-auto pt-12">
           <div className="mb-6 text-center">
                <h2 className="text-2xl font-bold text-slate-800">Scan Pickup QR</h2>
                <p className="text-slate-500">Verify handover from institution.</p>
           </div>
           <Card className="text-center shadow-lg border-emerald-100 relative overflow-hidden">
             <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-blue-500"></div>
             <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-600 border-4 border-slate-50">
               <QrCode size={32} />
             </div>
             <p className="text-sm text-slate-500 mb-6 bg-slate-50 p-3 rounded border border-slate-100">
                In a real device, this would open the camera. <br/>For this demo, paste the token generated by the Institution.
             </p>
             <form onSubmit={(e) => {
               e.preventDefault();
               const res = handleRecyclerScan(e.target.token.value);
               if(res.success) notify(`Success! Picked up ${res.log.weight_kg}kg of ${res.log.category}`, "success");
               else notify("Invalid or Expired QR Token", "error");
               e.target.reset();
             }}>
               <input name="token" className="w-full p-4 border-2 border-slate-200 focus:border-emerald-500 rounded-xl mb-4 text-center font-mono text-sm outline-none transition-colors" placeholder="Paste QR Token here..." required autoComplete="off" />
               <Button type="submit" className="w-full justify-center py-3 text-lg">Confirm Pickup</Button>
             </form>
           </Card>
        </div>
      </DashboardLayout>
    )
  }

  return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading App...</div>;
}

// --- SUB-COMPONENTS ---

const MetricCard = ({ label, value, sub, icon: Icon, color }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
    purple: "bg-purple-50 text-purple-600 border-purple-100"
  };
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 flex flex-col justify-between h-36 relative overflow-hidden">
       <div className={`absolute top-0 right-0 p-16 opacity-5 rounded-full -mr-8 -mt-8 ${colors[color].split(" ")[0]}`}></div>
       <div className="flex justify-between items-start relative z-10">
         <div className={`p-2.5 rounded-lg border ${colors[color]}`}><Icon size={20} /></div>
         <span className="text-3xl font-bold text-slate-800">{value}</span>
       </div>
       <div className="relative z-10">
         <div className="font-bold text-slate-700">{label}</div>
         <div className="text-xs text-slate-400 font-medium uppercase tracking-wide mt-1">{sub}</div>
       </div>
    </div>
  )
}

const FolderCard = ({ title, count, color }) => (
  <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm flex items-center gap-4 cursor-pointer hover:shadow-lg transition-all transform hover:-translate-y-1 group">
     <div className={`p-4 rounded-xl ${color} group-hover:scale-110 transition-transform`}>
       <FileText size={24} />
     </div>
     <div>
       <h3 className="font-bold text-slate-800 text-lg">{title}</h3>
       <p className="text-sm text-slate-400 group-hover:text-slate-500 transition-colors">{count} files</p>
     </div>
     <div className="ml-auto text-slate-300 group-hover:text-slate-400">
         <ArrowRight size={18}/>
     </div>
  </div>
);

const LeafIcon = ({ size=24, className="" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/>
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>
  </svg>
);

